import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createElementVNode as _createElementVNode, createVNode as _createVNode, createSlots as _createSlots } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = ["innerHTML"]

import type { PropType } from 'vue';
import { toRefs, ref, onBeforeUnmount, watch, computed } from 'vue';
import { useQuasar } from 'quasar';

// STORES
import { useUiStore } from 'src/stores/ui';

// Personal Components
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import QCustomInput from '@components/form/QCustomInput.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';

// Personal libs
import { $t } from 'src/i18n/utils';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';
import {
  IDiagGenericResponse,
  IQDialogButton,
  IQDialogPromptOption
} from '@shared/interface/qDialogTemplate.interface';
import { NotificationTypeEnum } from '@shared/enum/notification.enum';
import { IConfigSecurity } from '@shared/interface/config.interface';
import { InputFiedTypeEnum } from '@shared/enum/input.enum';
import { SecretTypeEnum } from '@shared/enum/secret-type.enum';

/**
 * Properties
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DiagGeneric',
  props: {
  show: { required: false, type: Boolean, default: false },
  manageLoading: { required: false, type: Boolean, default: false },
  title: { required: false, type: String, default: '' },
  message: { required: false, type: String, default: '' },
  persistent: { required: false, type: Boolean, default: false },
  html: { required: false, type: Boolean, default: false },
  passwordDense: { required: false, type: Boolean, default: false },
  dialogSize: {
    required: false,
    type: String as PropType<DialogSizeEnum>,
    default: DialogSizeEnum.SMALL,
  },
  ok: {
    required: false,
    type: Object as PropType<IQDialogButton>,
    default: () => ({ label: '' }),
  },
  cancel: {
    required: false,
    type: Object as PropType<IQDialogButton>,
    default: () => ({}),
  },
  hideCancel: { required: false, type: Boolean, default: false },
  hideClose: { required: false, type: Boolean, default: false },
  hideValid: { required: false, type: Boolean, default: false },
  inputPrompt: {
    required: false,
    type: Object as PropType<IQDialogPromptOption>,
    default: () => ({}),
  },
  notificationType: {
    required: false,
    type: String as PropType<NotificationTypeEnum>,
  },
},
  emits: ['close', 'cancel', 'valid'],
  setup(__props, { emit: __emit }) {

// Vendor
const props = __props;
const {
  show,
  manageLoading,
  title,
  message,
  persistent,
  html,
  dialogSize,
  ok,
  cancel,
  hideCancel,
  hideClose,
  inputPrompt,
  notificationType,
} = toRefs(props);

/**
 * Stores
 */
const uiStore = useUiStore();

/**
 * Emits
 */
const emits = __emit;

/**
 * Globals
 */
const $q = useQuasar();

/**
 * HTMLElement ref
 */
const qInputPassword = ref<typeof QCustomInput>();
const qInputUsername = ref<typeof QCustomInput>();
const qInputEmail = ref<typeof QCustomInput>();
const qInputTextarea = ref<typeof QCustomInput>();

/**
 * Datas
 */
const password = ref<string | undefined>(undefined);
const textarea = ref<string | undefined>(undefined);
const username = ref<string | undefined>(undefined);
const email = ref<string | undefined>(undefined);
const loadingOk = ref<boolean>(false);
const loadingCancel = ref<boolean>(false);

/**
 * Computed properties
 */
const securityRules = computed<IConfigSecurity | undefined>(
  () => uiStore.config?.security,
);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

function validForm() {
  if (inputPrompt.value?.type === InputFiedTypeEnum.EMAIL) {
    qInputEmail.value && qInputEmail.value?.blur()
    qInputEmail.value && qInputEmail.value?.validate()

    if (qInputEmail.value != null && qInputEmail.value.hasError()) {
      qInputEmail.value.focus()
      return false;
    }
  } else if(inputPrompt.value?.type === InputFiedTypeEnum.PASSWORD) {
    qInputPassword.value && qInputPassword.value?.blur()
    qInputPassword.value && qInputPassword.value?.validate()

    if (qInputPassword.value != null && qInputPassword.value.hasError()) {
      qInputPassword.value.focus()
      return false;
    }
  } else if(inputPrompt.value?.type === InputFiedTypeEnum.USERNAME) {
    qInputUsername.value && qInputUsername.value?.blur()
    qInputUsername.value && qInputUsername.value?.validate()

    if (qInputUsername.value != null && qInputUsername.value.hasError()) {
      qInputUsername.value.focus()
      return false;
    }
  } else if(inputPrompt.value?.type === InputFiedTypeEnum.TEXTAREA) {
    qInputTextarea.value && qInputTextarea.value?.blur()
    qInputTextarea.value && qInputTextarea.value?.validate()

    if (qInputTextarea.value != null && qInputTextarea.value.hasError()) {
      qInputTextarea.value.focus()
      return false;
    }
  }
  
  return true;
}

async function onOkClick() {
  if (!validForm()) {
    return;
  }

  if (manageLoading.value) {
    loadingOk.value = true;
  }

  const response: IDiagGenericResponse = {
    inputPassword: password.value,
    inputTextarea: textarea.value,
    inputEmail: email.value,
    inputUsername: username.value
  }

  if (ok.value.handler != null) {
    ok.value.handler(response);
  }

  if (ok.value.asyncHandler != null) {
    await ok.value.asyncHandler(response);
  }

  emits('valid', response);
}

async function onCancelClick() {
  if (manageLoading.value) {
    loadingCancel.value = true;
  }

  if (cancel.value.handler != null) {
    cancel.value.handler();
  }

  if (cancel.value.asyncHandler != null) {
    await cancel.value.asyncHandler();
  }

  emits('cancel');
}

/**
 * Watch
 */
watch(
  () => show.value,
  (newVal) => {
    if (newVal === true) {
      loadingCancel.value = false;
      loadingOk.value = false;
      password.value = undefined;
      textarea.value = undefined;
      email.value = undefined;
      username.value = undefined;
    }
  },
  { immediate: true },
);

/**
 * Hooks
 */
onBeforeUnmount(() => {
  password.value = undefined;
  textarea.value = undefined;
  email.value = undefined;
  username.value = undefined;
});

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    ref: "qDialogTemplate",
    "show-dialog": _unref(show),
    title: _unref(title),
    maximized: false,
    persistent: _unref(persistent),
    "display-close-button": _unref(hideClose) === false,
    "disable-close-button": loadingOk.value || loadingCancel.value,
    "dialog-size": _unref(dialogSize),
    "is-form": _unref(inputPrompt) != null,
    "notification-type": _unref(notificationType),
    onClose: _cache[4] || (_cache[4] = ($event: any) => (onClose()))
  }, _createSlots({
    content: _withCtx(() => [
      _createCommentVNode(" CONTENT - START "),
      (_ctx.$slots.custom)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _renderSlot(_ctx.$slots, "custom")
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (!_unref(html))
              ? (_openBlock(), _createBlock(_component_q_card_section, { key: 0 }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(message)), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }))
              : (_openBlock(), _createBlock(_component_q_card_section, { key: 1 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", { innerHTML: _unref(message) }, null, 8 /* PROPS */, _hoisted_3)
                  ]),
                  _: 1 /* STABLE */
                }))
          ])),
      _createCommentVNode(" CONTENT - END "),
      (_unref(inputPrompt).required)
        ? (_openBlock(), _createBlock(_component_q_card_section, {
            key: 2,
            class: "text-caption q-mx-sm q-my-none q-py-none"
          }, {
            default: _withCtx(() => _cache[5] || (_cache[5] = [
              _createTextVNode(" * : champs obligatoires ")
            ])),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (_unref(inputPrompt)?.type === _unref(InputFiedTypeEnum).PASSWORD)
        ? (_openBlock(), _createBlock(_component_q_card_section, { key: 3 }, {
            default: _withCtx(() => [
              _createVNode(QCustomInput, {
                modelValue: password.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((password).value = $event)),
                ref_key: "qInputPassword",
                ref: qInputPassword,
                "input-type": _unref(InputFiedTypeEnum).PASSWORD,
                "secret-type": _unref(SecretTypeEnum).NORMAL,
                "input-prompt": _unref(inputPrompt),
                disable: loadingCancel.value || loadingOk.value,
                "on-enter-key": onOkClick
              }, null, 8 /* PROPS */, ["modelValue", "input-type", "secret-type", "input-prompt", "disable"])
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (_unref(inputPrompt)?.type === _unref(InputFiedTypeEnum).EMAIL)
        ? (_openBlock(), _createBlock(_component_q_card_section, { key: 4 }, {
            default: _withCtx(() => [
              _createVNode(QCustomInput, {
                modelValue: email.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((email).value = $event)),
                ref_key: "qInputEmail",
                ref: qInputEmail,
                "input-type": _unref(InputFiedTypeEnum).EMAIL,
                "input-prompt": _unref(inputPrompt),
                disable: loadingCancel.value || loadingOk.value,
                "on-enter-key": onOkClick
              }, null, 8 /* PROPS */, ["modelValue", "input-type", "input-prompt", "disable"])
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (_unref(inputPrompt)?.type === _unref(InputFiedTypeEnum).USERNAME)
        ? (_openBlock(), _createBlock(_component_q_card_section, { key: 5 }, {
            default: _withCtx(() => [
              _createVNode(QCustomInput, {
                modelValue: username.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((username).value = $event)),
                ref_key: "qInputUsername",
                ref: qInputUsername,
                "input-type": _unref(InputFiedTypeEnum).USERNAME,
                "input-prompt": _unref(inputPrompt),
                disable: loadingCancel.value || loadingOk.value,
                "on-enter-key": onOkClick
              }, null, 8 /* PROPS */, ["modelValue", "input-type", "input-prompt", "disable"])
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (_unref(inputPrompt)?.type === _unref(InputFiedTypeEnum).TEXTAREA)
        ? (_openBlock(), _createBlock(_component_q_card_section, { key: 6 }, {
            default: _withCtx(() => [
              _createVNode(QCustomInput, {
                modelValue: textarea.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((textarea).value = $event)),
                ref_key: "qInputTextarea",
                ref: qInputTextarea,
                "input-type": _unref(InputFiedTypeEnum).TEXTAREA,
                "input-prompt": _unref(inputPrompt),
                disable: loadingCancel.value || loadingOk.value,
                required: _unref(inputPrompt)?.required
              }, null, 8 /* PROPS */, ["modelValue", "input-type", "input-prompt", "disable", "required"])
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true)
    ]),
    _: 2 /* DYNAMIC */
  }, [
    (!_unref(hideCancel) || !__props.hideValid)
      ? {
          name: "footer",
          fn: _withCtx(() => [
            _createVNode(_component_q_separator),
            _createVNode(_component_q_card_actions, { align: "right" }, {
              default: _withCtx(() => [
                (!_unref(hideCancel))
                  ? (_openBlock(), _createBlock(QBtnCancel, {
                      key: 0,
                      label: _unref(cancel).label ? _unref(cancel).label : _unref($t)('generic.btn.cancel.label'),
                      disable: loadingOk.value || loadingCancel.value,
                      loading: loadingCancel.value,
                      "icon-left": _unref(cancel)?.iconLeft,
                      "icon-right": _unref(cancel)?.iconRight,
                      onClick: onCancelClick
                    }, null, 8 /* PROPS */, ["label", "disable", "loading", "icon-left", "icon-right"]))
                  : _createCommentVNode("v-if", true),
                _createVNode(QBtnValid, {
                  label: _unref(ok).label ? _unref(ok).label : _unref($t)('generic.btn.validate.label'),
                  disable: loadingOk.value || loadingCancel.value,
                  loading: loadingOk.value,
                  "loading-label": '',
                  "icon-left": _unref(ok)?.iconLeft,
                  "icon-right": _unref(ok)?.iconRight,
                  "notification-type": _unref(notificationType),
                  onClick: onOkClick
                }, null, 8 /* PROPS */, ["label", "disable", "loading", "icon-left", "icon-right", "notification-type"])
              ]),
              _: 1 /* STABLE */
            })
          ]),
          key: "0"
        }
      : undefined
  ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["show-dialog", "title", "persistent", "display-close-button", "disable-close-button", "dialog-size", "is-form", "notification-type"]))
}
}

})